import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import useWindowSize from '@hooks/use-window-size';
import { accentFold } from '@helpers/utils/general';
import { HotFilterItem } from '@interfaces/api/responses/hot-filters.response';
import {
  calculateHotFiltersTotalCheckedItems,
  resetHotfiltersItemsToUnchecked,
} from '@helpers/utils/hot-filters/hot-filters';
import HotFilterTabs from '@components/organisms/hot-filters/hot-filter-tabs/hot-filter-tabs';
import HotFilterItems from '@components/organisms/hot-filters/hot-filter-items/hot-filter-items';
import HotFilterSearchInput from '@components/organisms/hot-filters/hot-filter-search-input/hot-filter-search-input';
import { getTranslatedHotFilterName } from '@helpers/utils/hot-filters/hot-filters-parsing-util';
import HotFilterDialog from '@components/organisms/hot-filters/hot-filter-dialog/hot-filter-dialog';
import { updateSelectedState } from '@helpers/utils/hot-filters/hot-filters-selection';
import styles from './hot-filter-list.module.scss';

type HotFilterListProps = {
  item?: HotFilterItem;
  tabsData?: HotFilterItem;
  onApply?: (dataItem: HotFilterItem) => void;
  isDialogOpened?: boolean;
  onClose: () => void;
  listIndex: number;
  updateCheckedCounts: (index: number, count: number) => void;
  dataCy: string;
  showSearchBar?: boolean;
};

const HotFilterList: React.FC<HotFilterListProps> = ({
  item,
  tabsData,
  onApply,
  isDialogOpened,
  onClose,
  listIndex,
  updateCheckedCounts,
  dataCy,
  showSearchBar = false,
}) => {
  const { t } = useTranslation();
  const { isWiderThanMd } = useWindowSize();
  const searchEl = useRef(null);

  const [dataItem, setDataItem] = useState<HotFilterItem>(item);
  const [filteredChildren, setFilteredChildren] = useState<HotFilterItem[]>(item?.children);
  const [clonedChildren, setClonedChildren] = useState<HotFilterItem[]>(item?.children);
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const childrenBySelectedTab = useRef<Record<string, HotFilterItem[]>>({});

  const checkedCounts = useMemo(() => {
    const children = dataItem.id === 'category' ? Object.values(childrenBySelectedTab.current).flat() : clonedChildren;
    return calculateHotFiltersTotalCheckedItems({ ...dataItem, children });
  }, [dataItem, clonedChildren]);

  useEffect(() => {
    const initiallyCheckedTab = tabsData?.children?.find((child) => child.isChecked);
    setSelectedTab(initiallyCheckedTab?.id || tabsData?.children?.[0]?.id);
  }, [tabsData?.children]);

  useEffect(() => {
    setDataItem(item);

    if (tabsData?.children?.length) {
      const childrenForTab = (childrenBySelectedTab.current[selectedTab] ?? dataItem?.children).filter(
        (child) => child.parentID === selectedTab,
      );
      setFilteredChildren(childrenForTab);
      setClonedChildren(childrenForTab);
      tabsData?.children?.forEach((tab) => {
        const childrenForTab = item?.children?.filter((child) => child.parentID === tab.id);
        childrenBySelectedTab.current = { ...childrenBySelectedTab.current, [tab.id]: childrenForTab };
      });
    } else {
      setFilteredChildren(item?.children);
      setClonedChildren(item?.children);
    }
  }, [item, isDialogOpened]);

  useEffect(() => {
    if (tabsData?.children?.length) {
      const childrenForTab = (childrenBySelectedTab.current[selectedTab] ?? dataItem?.children).filter(
        (child) => child.parentID === selectedTab,
      );
      setFilteredChildren(childrenForTab);
      setClonedChildren(childrenForTab);
    }
  }, [tabsData?.children, selectedTab]);

  const handleExpand = (id: string): void => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [id]: !prevExpanded[id],
    }));
  };

  const handleTabChange = (tab: HotFilterItem): void => {
    setExpanded({});
    setSelectedTab(tab?.id);
  };

  const handleClear = useCallback((): void => {
    const uncheckedChildren = resetHotfiltersItemsToUnchecked(
      tabsData?.children?.length ? Object.values(childrenBySelectedTab.current).flat() : clonedChildren,
    );

    tabsData?.children?.forEach((tab) => {
      const childrenForTab = uncheckedChildren.filter((child) => child.parentID === tab.id);
      childrenBySelectedTab.current[tab.id] = childrenForTab;
    });

    let updatedChildren = tabsData?.children?.length
      ? uncheckedChildren.filter((fItem) => fItem?.parentID === selectedTab)
      : uncheckedChildren;

    // If there is an active search, apply filtering again
    if (searchTerm) {
      updatedChildren = updatedChildren.filter((child) =>
        accentFold(child.name.toLowerCase()).includes(accentFold(searchTerm.toLowerCase())),
      );
    }

    setFilteredChildren(updatedChildren);
    setClonedChildren(uncheckedChildren);
  }, [tabsData, selectedTab, searchTerm]);

  const handleSearchReset = (): void => {
    setSearchTerm('');
    setFilteredChildren([...clonedChildren]);
  };

  const filterChildren = (term: string): void => {
    if (!term) {
      setFilteredChildren(clonedChildren);
      return;
    }

    // Filter based on search term but keep the original checked states
    const filtered = clonedChildren
      .map((child) => ({
        ...child,
        isChecked: clonedChildren.find((c) => c.id === child.id)?.isChecked ?? child.isChecked,
      }))
      .filter((child) => accentFold(child.name.toLowerCase()).includes(accentFold(term.toLowerCase())));

    setFilteredChildren(filtered);
  };

  const handleSearch = (e): void => {
    setSearchTerm(e);
    filterChildren(e);
  };

  const handleCheck = (childItem: HotFilterItem): void => {
    const updatedClonedChildren = updateSelectedState(clonedChildren, childItem);
    setClonedChildren(updatedClonedChildren);
    setFilteredChildren(updateSelectedState(filteredChildren, childItem));

    childrenBySelectedTab.current = {
      ...childrenBySelectedTab.current,
      [selectedTab]: updatedClonedChildren,
    };
  };

  const applyChange = (): void => {
    const updatedChildren = tabsData?.children?.length
      ? Object.values(childrenBySelectedTab.current)?.flat() || clonedChildren
      : clonedChildren;

    const updatedDataItem = {
      ...dataItem,
      children: updatedChildren,
    };
    const count = calculateHotFiltersTotalCheckedItems(updatedDataItem);
    updateCheckedCounts(listIndex, count);
    onApply(updatedDataItem);
  };

  const handleClose = (): void => {
    setClonedChildren(filteredChildren);
    childrenBySelectedTab.current = {};
    const initiallyCheckedTab = tabsData?.children?.find((child) => child.isChecked);
    setSelectedTab(initiallyCheckedTab?.id || tabsData?.children?.[0]?.id);
    onClose();
  };

  return (
    <HotFilterDialog
      headerLabel={getTranslatedHotFilterName(item.name)}
      isDialogOpened={isDialogOpened}
      onClose={handleClose}
      handleApplyChange={applyChange}
      handleClear={handleClear}
      isClearCtaDisabled={checkedCounts <= 0}
      dataCy={dataCy}
    >
      {showSearchBar && (
        <HotFilterSearchInput
          value={searchTerm}
          ref={searchEl}
          placeholder={
            item.name === 'brand'
              ? t('FAVORITE_PAGE.FILTER_SEARCH.BRANDS')
              : `${t('CATALOG.FILTERS.SEARCH_PLACEHOLDER.DEFAULT')} ${t(getTranslatedHotFilterName(item.name))}`
          }
          onChange={handleSearch}
          onReset={handleSearchReset}
          identifier={`${dataCy}-search-input`}
        />
      )}
      {tabsData?.children && (
        <HotFilterTabs
          data={tabsData?.children}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          dataCy={`${dataCy}-tabs`}
        />
      )}
      {(filteredChildren || clonedChildren).length > 0 ? (
        <div
          className={clsx(
            styles.hotFilterList__itemsWrapper,
            !isWiderThanMd && styles['hotFilterList__itemsWrapper--mobileWrapper'],
          )}
        >
          <HotFilterItems
            childItem={searchTerm ? filteredChildren : clonedChildren}
            item={item}
            expanded={expanded}
            handleExpand={handleExpand}
            handleCheck={handleCheck}
            dataCy={dataCy}
          />
        </div>
      ) : (
        <div className={styles.hotFilterList__noResultsContainer}>{t('FAVORITE_PAGE.FILTER_SEARCH.RESULTS_EMPTY')}</div>
      )}
    </HotFilterDialog>
  );
};

export default HotFilterList;
